import IConfig, { IEmbeddedAppConfig } from '@sitegen/common/constants/config';
import { IAvailableLanguages } from '@sitegen/common/localization/language-regions';

import i18nService from './i18n.service';
import { WindowHistoryService } from 'utils/window-history.service';

import { resolveBasePathname } from './language-provider.utils';
import { getCountryFromLanguage, getSafeLocale, getShortLanguageCode } from 'modules/language-provider/locale';

import { APPLY_LANGUAGE, REPORT_ANALYTICS_LANGUAGE_AND_COUNTRY } from './language-provider.constants';
import { DEFAULT_LANGUAGE } from 'constants/locales';
import { UbisoftOverlayService } from 'modules/ubisoft-overlay-service/service';
import { Dispatch } from 'types/dispatch';

/**
 * Using for detecting and loading initial language from url or from a host environment.
 */
export const fetchInitialLanguage = () => {
    return (dispatch: Dispatch, getState: Function): void => {
        const availableLocales: IAvailableLanguages = (getState().config as IConfig).availableLanguages;
        const initialLanguage: string | undefined = (getState().embeddedAppConfig as IEmbeddedAppConfig).initialLanguage;

        const locale: string = getSafeLocale({
            useUrl: !initialLanguage,
            useBrowserFallback: true,
            useAvailableLocales: availableLocales,
            language: initialLanguage
        });

        dispatch(applyLanguage(locale));
    };
};

/**
 * Using for applying language, for example, in language picker in footer.component.
 */
export const fetchLanguage = (languageCode: string) => (
    dispatch: Dispatch
): void => {
    dispatch(applyLanguage(languageCode));

    dispatch({
        type: REPORT_ANALYTICS_LANGUAGE_AND_COUNTRY,
        payload: {
            language: getShortLanguageCode({ language: languageCode }),
            country: getCountryFromLanguage({ language: languageCode })
        }
    });
};

/**
 * Private language-provider actions.
 */
export const applyLanguage = (languageCode: string) => async (
    dispatch: Dispatch, getState: Function
): Promise<void> => {
    const {
        dictionaries,
        config: { urlPath, contentPath }
    } = getState();

    // resolve base pathname
    const basePath = resolveBasePathname(urlPath);
    const translationsPath = dictionaries[languageCode]
        ? `${contentPath}${dictionaries[languageCode]}` : null;
    const fallbackTranslationsPath = dictionaries[DEFAULT_LANGUAGE]
        ? `${contentPath}${dictionaries[DEFAULT_LANGUAGE]}` : null;

    UbisoftOverlayService.changeLocale(languageCode);

    try {
        const newLanguageCode = await i18nService.changeLanguage({
            languageCode,
            translationsPath,
            fallbackTranslationsPath
        });

        WindowHistoryService.pushState(basePath, newLanguageCode);
        dispatch({ type: APPLY_LANGUAGE, payload: newLanguageCode });
    } catch (err) {
        WindowHistoryService.pushState(basePath, DEFAULT_LANGUAGE);
        console.warn(`Can't find translation for ${languageCode} language code`);
    }
};

export type ApplyLanguage = {
    type: 'APPLY_LANGUAGE',
    payload: string
};
